/**
 * The recording screen — one response at a time.
 *
 * Deliberately single-card: the backend hands out one active slot plus one
 * buffered, so there is never a list to choose from. That keeps the decision
 * simple (record this, or skip it) and means a contributor never sits on a
 * pile of content nobody else can reach.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { IconCheck, IconClock, IconMic, IconPause, IconPlay, IconRefresh, IconStop, IconTrash, SendIcon } from "../components/Icons";
import { Loading } from "../components/Loading";
import { VoiceWave, type VoiceWaveMode } from "../components/VoiceWave";
import { ClipPlayer } from "../components/ClipPlayer";
import { useToast } from "../hooks/useToast";
import { assignments as assignmentsApi, earnings as earningsApi, recordings as recordingsApi } from "../api/endpoints";
import { useRecorder } from "../hooks/useRecorder";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { useConfig } from "../store/config";
import { formatDuration } from "../lib/validation";
import { ApiError } from "../api/http";
import type {
  AssignmentCard,
  AssignmentProgress,
  ClaimResponse,
  EarningsSummary,
} from "../types/api";


const WAVE_BARS = 56;

export function Record() {
  const toast = useToast();
  // Limits come from the API, so the composer cannot promise something the
  // API will refuse — which is exactly how a 3-minute cap once shipped
  // against a 60-second rule.
  const { recording: limits, text_response: textLimits } = useConfig(
    (state) => state.config,
  );
  const MIN_SECONDS = limits.min_seconds;
  const MAX_SECONDS = limits.max_seconds;
  const IDEAL_MIN = limits.ideal_min_seconds;
  const IDEAL_MAX = limits.ideal_max_seconds;
  const MAX_TEXT = textLimits.max_length;
  const recorder = useRecorder(MAX_SECONDS, WAVE_BARS);
  // "Record this again" from My Log links straight to one assignment, rather
  // than relying on it happening to be the next card.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedAssignment = searchParams.get("assignment");

  const [assignment, setAssignment] = useState<AssignmentCard | null>(null);
  const [progress, setProgress] = useState<AssignmentProgress | null>(null);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"submitting" | "skipping" | null>(null);
  // Reply tasks accept a typed answer as well as a spoken one.
  const [mode, setMode] = useState<"voice" | "text">("voice");
  const [replyText, setReplyText] = useState("");
  const [exhausted, setExhausted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clipPlaybackReset, setClipPlaybackReset] = useState(0);
  const resetRecorder = recorder.reset;
  const takeRef = useRef<HTMLAudioElement | null>(null);
  const [takePlaying, setTakePlaying] = useState(false);
  const [takeElapsed, setTakeElapsed] = useState(0);
  const takeUrl = recorder.clip?.url;

  useEffect(() => {
    setTakePlaying(false);
    setTakeElapsed(0);
  }, [takeUrl]);

  // Bumping this re-runs the loader. The fetch lives inside the effect so
  // every state update happens after the await and is skipped once
  // cancelled — a contributor who navigates away mid-claim never gets a
  // card written into an unmounted screen.
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  const applyClaim = useCallback((result: ClaimResponse) => {
    setProgress(result.progress);
    const open = result.assignments.find((item) => item.status === "assigned");
    setAssignment(open ?? null);
    setExhausted(!open);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // A deep-linked assignment wins: it is the one the person asked for.
        if (requestedAssignment) {
          const card = await assignmentsApi.get(requestedAssignment);
          if (cancelled) return;
          setAssignment(card);
          setExhausted(false);
          setError(null);
          setLoading(false);
          return;
        }
        const result = await assignmentsApi.claim(1);
        if (cancelled) return;
        applyClaim(result);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === "no_content_available") {
          setAssignment(null);
          setExhausted(true);
          setError(null);
        } else {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }

      // The meters are a nicety — never let them block or break recording.
      try {
        const summaryResult = await earningsApi.summary();
        if (!cancelled) setSummary(summaryResult);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce, applyClaim, requestedAssignment]);

  // A new card means a clean recorder — otherwise the previous take would
  // still be sitting there, ready to be submitted against the wrong prompt.
  const currentAssignmentId = assignment?.assignment_id;
  // The recorder is an external system (a live microphone), so it is reset in
  // an effect. The composer is plain state, so it is adjusted during render —
  // React's own pattern for "reset state when a prop changes", and it avoids
  // the extra render an effect would cause.
  const [composerFor, setComposerFor] = useState(currentAssignmentId);
  if (currentAssignmentId !== composerFor) {
    setComposerFor(currentAssignmentId);
    setReplyText("");
    setMode("voice");
  }

  useEffect(() => {
    resetRecorder();
  }, [currentAssignmentId, resetRecorder]);

  async function handleSubmit() {
    if (!assignment || !recorder.clip) return;
    if (recorder.clip.durationSeconds < MIN_SECONDS) {
      toast.show(`Recordings need to be at least ${MIN_SECONDS} seconds.`, "error");
      return;
    }

    setBusy("submitting");
    try {
      const filename =
        `${assignment.content.item_id}-r${assignment.content.response_number}` +
        `.${recorder.clip.extension}`;
      await recordingsApi.submit(
        assignment.assignment_id,
        recorder.clip.blob,
        filename,
        recorder.clip.durationSeconds,
      );
      toast.show("Sent for review. We'll let you know once it's accepted.", "success");
      setSubmitted(true);
      if (requestedAssignment) setSearchParams({}, { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError && err.code === "duplicate_recording"
          ? "You've already sent a recording for this one."
          : (err as Error).message;
      toast.show(message, "error");
      if (err instanceof ApiError && err.code === "duplicate_recording") {
        recorder.reset();
        refresh();
      }
    } finally {
      setBusy(null);
    }
  }

  // Only while nothing is in flight. A refresh mid-take would swap the card
  // out from under a recording that has not been sent — and unlike the other
  // screens, that work only exists in this tab.
  const safeToRefresh =
    busy === null &&
    recorder.state !== "recording" &&
    recorder.state !== "encoding" &&
    recorder.clip === null &&
    replyText.trim() === "" &&
    !requestedAssignment;

  useRefreshOnFocus(refresh, { enabled: safeToRefresh });

  async function handleSendText() {
    if (!assignment || !replyText.trim()) return;
    setBusy("submitting");
    try {
      await recordingsApi.submitText(assignment.assignment_id, replyText.trim());
      toast.show("Sent for review. We'll let you know once it's accepted.", "success");
      setReplyText("");
      if (requestedAssignment) setSearchParams({}, { replace: true });
      refresh();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  async function handleSkip() {
    if (!assignment) return;
    setClipPlaybackReset((value) => value + 1);
    setBusy("skipping");
    try {
      if (requestedAssignment) setSearchParams({}, { replace: true });
      applyClaim(await assignmentsApi.skip(assignment.assignment_id));
      recorder.reset();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Layout title="Record"><Loading label="Finding your next response…" /></Layout>;

  function recordAnother() {
    setSubmitted(false);
    recorder.reset();
    refresh();
  }

  const card = assignment?.content;
  const tooShort = recorder.clip ? recorder.clip.durationSeconds < MIN_SECONDS : false;
  const shortOfIdeal = recorder.clip
    ? recorder.clip.durationSeconds < IDEAL_MIN && !tooShort
    : false;

  const isRecording = recorder.state === "recording";
  const waveMode: VoiceWaveMode = isRecording
    ? recorder.paused
      ? "paused"
      : "recording"
    : recorder.clip
      ? "ready"
      : "idle";
  const timer = isRecording
    ? formatDuration(recorder.seconds)
    : recorder.clip
      ? formatDuration(recorder.clip.durationSeconds)
      : "0:00";
  const takeProgress =
    recorder.clip && recorder.clip.durationSeconds > 0
      ? Math.min(1, takeElapsed / recorder.clip.durationSeconds)
      : 0;

  const sentDone = progress?.completed ?? summary?.recordings_total ?? 0;
  const sentTarget = progress?.target ?? summary?.target ?? 0;
  const sentPercent = sentTarget > 0 ? Math.min(100, (sentDone / sentTarget) * 100) : 0;

  async function toggleTake() {
    const audio = takeRef.current;
    if (!audio) return;
    if (takePlaying) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch {
      toast.show("Couldn't play the recording back.", "error");
    }
  }

  return (
    <Layout title="Record">
      <section className="page record-page">
        <div className="page-kicker">RECORD</div>
        <h1>Respond to this clip</h1>
        <div className={submitted ? "record-layout is-submitted" : "record-layout"}>
          <div className="record-main">
          {error && (
            <div className="panel mt-8">
              <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={refresh}>
                Try again
              </button>
            </div>
          )}

          {!error && exhausted && (
            <div className="panel empty">
              <h3>You're all caught up</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                There's nothing left for you to record right now. New content is added
                regularly — check back soon.
              </p>
              <button type="button" className="btn btn-ghost mt-16" onClick={refresh}>
                Check again
              </button>
            </div>
          )}

          {!error && card && assignment && (
            <>
              <article className="rec-card rec-context">
                <span className="rec-label">
                  {card.is_reply_to_me
                    ? "Someone replied to you"
                    : card.source === "platform"
                      ? "Replying to a listener"
                      : "Responding to"}
                </span>
                <h2 className="rec-title">{card.title}</h2>

                {assignment.is_rerecord && (
                  <div className="rerecord-banner">
                    <div className="rerecord-title">
                      Take {assignment.attempt} — a reviewer asked for this one again
                    </div>
                    {assignment.rerecord_note && (
                      <p className="rerecord-note">“{assignment.rerecord_note}”</p>
                    )}
                    <p className="rerecord-hint">
                      Record it again below. Your earlier take is replaced, and this one
                      goes back for review.
                    </p>
                  </div>
                )}

                {card.clip_url && (
                  <ClipPlayer
                    key={`${assignment.assignment_id}-${clipPlaybackReset}`}
                    url={card.clip_url}
                    durationSeconds={card.clip_duration_seconds}
                    variant="record"
                  />
                )}

                {card.reply_to?.author && (
                  <div className="reply-meta muted">
                    {card.is_reply_to_me ? "From" : "Originally posted by"} @
                    {card.reply_to.author.user_handle ?? "listener"}
                  </div>
                )}
              </article>

              {submitted ? (
                <div className="rec-card rec-submitted">
                  <div className="check-orb" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <path d="M5 11.2 9.2 15.4 17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2>Voice note submitted</h2>
                  <p>Your recording has been sent for review.</p>
                  <div className="in-review"><IconClock width={14} height={14} /> In review</div>
                  <button type="button" className="btn btn-primary" onClick={recordAnother}>
                    Record another response <IconMic width={16} height={16} />
                  </button>
                </div>
              ) : (
              <article className={`rec-card rec-box is-${waveMode}`}>
                <span className="rec-label rec-label-center">
                  {card.allows_text && mode === "text" ? (
                    "Write your reply"
                  ) : (
                    <>
                      <span className="note-long">Record your note</span>
                      <span className="note-short">Record your response</span>
                    </>
                  )}
                </span>
                <p className="rec-prompt">{card.text}</p>

                {card.allows_text && (
                  <div className="composer-switch">
                    <button
                      type="button"
                      className={`chip ${mode === "voice" ? "chip-active" : ""}`}
                      onClick={() => setMode("voice")}
                      disabled={isRecording}
                    >
                      Record a reply
                    </button>
                    <button
                      type="button"
                      className={`chip ${mode === "text" ? "chip-active" : ""}`}
                      onClick={() => setMode("text")}
                      disabled={isRecording}
                    >
                      Write a reply
                    </button>
                    <span className="composer-note muted">Both paid the same</span>
                  </div>
                )}

                {card.allows_text && mode === "text" ? (
                  <>
                    <textarea
                      className="reply-box"
                      rows={5}
                      value={replyText}
                      maxLength={MAX_TEXT}
                      placeholder="Write your reply…"
                      onChange={(event) => setReplyText(event.target.value)}
                    />
                    <div className="row-between">
                      <span className="muted" style={{ fontSize: 12 }}>
                        {replyText.trim().length}/{MAX_TEXT}
                      </span>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ width: "auto" }}
                        disabled={busy !== null || replyText.trim().length < 2}
                        onClick={() => void handleSendText()}
                      >
                        {busy === "submitting" ? "Sending…" : "Send for review →"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rec-timer">{timer}</div>

                    <div className={`rec-status is-${waveMode}`}>
                      {waveMode === "recording" ? (
                        <><i className="rec-dot" /> Recording</>
                      ) : waveMode === "paused" ? (
                        <><IconPause width={12} height={12} /> Paused</>
                      ) : recorder.state === "encoding" ? (
                        <>Processing…</>
                      ) : recorder.clip ? (
                        <><IconCheck width={14} height={14} /> Recording complete</>
                      ) : (
                        <><IconClock width={13} height={13} /> Aim for {IDEAL_MIN}–{IDEAL_MAX} seconds</>
                      )}
                    </div>

                    <div className="rec-wave-row">
                      <VoiceWave levels={recorder.levels} mode={waveMode} progress={takeProgress} />
                    </div>

                    {recorder.clip && (
                      <audio
                        ref={takeRef}
                        src={recorder.clip.url}
                        preload="metadata"
                        onPlay={() => setTakePlaying(true)}
                        onPause={() => setTakePlaying(false)}
                        onTimeUpdate={(event) => setTakeElapsed(event.currentTarget.currentTime)}
                        onEnded={() => {
                          setTakePlaying(false);
                          setTakeElapsed(0);
                        }}
                      />
                    )}

                    {recorder.error && <div className="form-error">{recorder.error}</div>}
                    {tooShort && (
                      <div className="form-error">
                        That's under {MIN_SECONDS} seconds — record a longer take.
                      </div>
                    )}
                    {shortOfIdeal && (
                      <div className="hint" style={{ color: "var(--warn)" }}>
                        That's quite short. Around {IDEAL_MIN}–{IDEAL_MAX} seconds works best.
                      </div>
                    )}

                    <div className={`rec-actions is-${waveMode}`}>
                      {isRecording ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-ghost rec-secondary"
                            onClick={recorder.paused ? recorder.resume : recorder.pause}
                          >
                            {recorder.paused ? "Resume" : "Pause"}
                            {recorder.paused ? <IconPlay width={14} height={14} /> : <IconPause width={14} height={14} />}
                          </button>
                          <button type="button" className="btn btn-primary rec-primary" onClick={recorder.stop}>
                            Stop recording <IconStop width={14} height={14} />
                          </button>
                        </>
                      ) : recorder.clip ? (
                        <>
                          <div className="rec-take-row">
                            <button
                              type="button"
                              className="btn btn-ghost rec-secondary"
                              onClick={() => void toggleTake()}
                            >
                              {takePlaying ? "Pause" : "Play"}
                              {takePlaying ? <IconPause width={14} height={14} /> : <IconPlay width={14} height={14} />}
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost rec-secondary"
                              onClick={() => void recorder.start()}
                              disabled={busy !== null}
                            >
                              <span className="again-long">Record again</span>
                              <span className="again-short">Again</span>
                              <IconRefresh width={14} height={14} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost rec-secondary"
                              onClick={recorder.reset}
                              disabled={busy !== null}
                            >
                              Delete <IconTrash width={14} height={14} />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary rec-submit"
                            onClick={() => void handleSubmit()}
                            disabled={busy !== null || tooShort}
                          >
                            {busy === "submitting" ? "Sending…" : "Submit response"} <SendIcon width={16} height={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary rec-primary"
                            onClick={() => void recorder.start()}
                            disabled={recorder.state === "encoding" || recorder.state === "requesting"}
                          >
                            {recorder.state === "requesting"
                              ? "Starting…"
                              : recorder.state === "encoding"
                                ? "Processing…"
                                : "Start recording"}
                            <MicGlyph />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost rec-secondary"
                            onClick={() => void handleSkip()}
                            disabled={busy !== null}
                          >
                            {busy === "skipping" ? "Finding another…" : "Skip this one"}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}

                {card.allows_text && mode === "text" && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm skip-btn"
                    onClick={() => void handleSkip()}
                    disabled={busy !== null}
                  >
                    {busy === "skipping" ? "Finding another…" : "Skip this one"}
                  </button>
                )}
              </article>
              )}
            </>
          )}
          </div>

          <aside className="rec-side">
            <div className="rec-stat rec-progress">
              <span className="rec-progress-text">
                {sentDone} of {sentTarget || "—"} voice notes sent
              </span>
              <div className="rec-progress-track">
                <div className="rec-progress-fill" style={{ width: `${sentPercent}%` }} />
              </div>
            </div>
            <div className="rec-stats">
              <div className="rec-stat">
                <span className="rec-stat-k">Sent</span>
                <strong className="rec-stat-v">
                  {summary ? `${summary.recordings_total} / ${summary.target}` : "—"}
                </strong>
              </div>
              <div className="rec-stat">
                <span className="rec-stat-k">Rate</span>
                <strong className="rec-stat-v">{summary ? `$${summary.rate.formatted}` : "—"}</strong>
                <span className="rec-stat-sub">per approved voice note.</span>
              </div>
              <div className="rec-stat">
                <span className="rec-stat-k">Available</span>
                <strong className="rec-stat-v">{summary ? `$${summary.balance.formatted}` : "—"}</strong>
                <span className="rec-stat-sub">Approved earnings ready to redeem</span>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}

function MicGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  );
}
